import type { FormItemConfig } from '@ms/shared-components/comps/CompsForm';
import { PasswordInput } from '../utils';

/**
 * 千问版本选项（与后端 QWEN_VERSION_MODELS 对应）
 */
export const QWEN_VERSION_OPTIONS = [
  { value: '3.8', label: 'Qwen 3.8（推荐：max/flash）' },
  { value: '3.7', label: 'Qwen 3.7（plus/flash/max）' },
  { value: '3.6', label: 'Qwen 3.6（plus/flash）' },
  { value: '3.5', label: 'Qwen 3.5（plus/flash）' },
  { value: '3', label: 'Qwen 3（max/32b/8b）' },
  { value: 'legacy', label: '旧版（qwen-plus/max/turbo）' },
];

/**
 * 各版本默认模型（与后端 QWEN_VERSION_MODELS 对应）
 * 用于在表单中自动填充 model 字段
 */
export const QWEN_VERSION_DEFAULT_MODELS: Record<string, Record<string, string>> = {
  '3.8': { small: 'qwen3.8-flash', medium: 'qwen3.7-plus', large: 'qwen3.8-max' },
  '3.7': { small: 'qwen3.7-flash', medium: 'qwen3.7-plus', large: 'qwen3.7-max' },
  '3.6': { small: 'qwen3.6-flash', medium: 'qwen3.6-plus', large: 'qwen3.6-max-preview' },
  '3.5': { small: 'qwen3.5-flash', medium: 'qwen3.5-plus', large: 'qwen3.5-plus' },
  '3': { small: 'qwen3-8b', medium: 'qwen3-32b', large: 'qwen3-max' },
  legacy: { small: 'qwen-turbo', medium: 'qwen-plus', large: 'qwen-max' },
};

/**
 * 表单配置项
 */
export const getFormItems = (): FormItemConfig[] => [
  {
    name: 'name',
    label: '资源名称',
    type: 'input',
    rules: [{ required: true, message: '请输入资源名称' }],
    props: { placeholder: '例如：Qwen 3.8 旗舰' },
  },
  {
    name: 'provider',
    label: '提供商',
    type: 'select',
    rules: [{ required: true, message: '请选择提供商' }],
    options: [
      { value: 'qwen', label: 'Qwen (阿里云)' },
      { value: 'openai', label: 'OpenAI' },
      { value: 'anthropic', label: 'Anthropic (Claude)' },
      { value: 'custom', label: '自定义' },
    ],
  },
  {
    name: 'qwenVersion',
    label: '千问版本',
    type: 'select',
    options: QWEN_VERSION_OPTIONS,
    props: { placeholder: '选择千问版本，自动填充模型名称' },
    // 仅当 provider 为 qwen 时显示
    hidden: (values: any) => values?.provider !== 'qwen',
    dependencies: ['provider'],
  },
  {
    name: 'model',
    label: '模型名称',
    type: 'input',
    rules: [{ required: true, message: '请输入模型名称' }],
    props: { placeholder: '例如：qwen3.7-plus, qwen3.8-max, gpt-4' },
  },
  {
    name: 'modelTier',
    label: '模型档位',
    type: 'select',
    rules: [{ required: true, message: '请选择模型档位（小/中/大）' }],
    options: [
      { value: 'small', label: '小（省成本）' },
      { value: 'medium', label: '中（默认平衡）' },
      { value: 'large', label: '大（高能力）' },
    ],
  },
  {
    name: 'endpoint',
    label: 'API 端点',
    type: 'input',
    rules: [{ required: true, message: '请输入 API 端点' }],
    props: { placeholder: '例如：https://dashscope.aliyuncs.com/api/v1/services/aigc/text-generation/generation' },
  },
  {
    name: 'apiKey',
    label: 'API 密钥',
    type: 'custom',
    rules: [{ required: true, message: '请输入 API 密钥' }],
    props: { placeholder: '输入 API 密钥' },
    customComponent: PasswordInput,
  },
  {
    name: 'priority',
    label: '优先级',
    type: 'number',
    props: { min: 0, max: 100, style: { width: '100%' } },
  },
  {
    name: 'tags',
    label: '标签（逗号分隔）',
    type: 'input',
    props: { placeholder: '例如：default,internal,premium' },
  },
  {
    name: 'description',
    label: '描述',
    type: 'textarea',
    props: { rows: 3, placeholder: '资源描述' },
  },
  {
    name: 'enabled',
    label: '启用',
    type: 'switch',
    valuePropName: 'checked',
    props: { checkedChildren: '启用', unCheckedChildren: '禁用' },
  },
];

/**
 * 表单配置
 */
export const formConfig = {
  layout: 'horizontal' as const,
  contentPadding: 0,
};
